import React from "react";
import PropTypes from "prop-types";
import MegaMenuResultsContainer from "./mega-menu-results";
import SearchBar from "./search-bar";
import Icon from "../custom-widgets/icon";

import styles from "./search-bar.module.scss";

/**
 * MegaMenuSearchBar component
 *
 * @param {Object} props - The component props
 * @param {string} props.type - The type of the search bar (default: "mobile")
 * @param {string} props.query - The current search query
 * @param {Object} props.querySuggestions - The query suggestions object containing hits
 * @param {boolean} props.shouldFocus - Flag to determine if the search bar should be focused
 * @param {Function} props.shouldFocusCallback - Callback function to handle focus state
 * @param {Function} props.clearQuery - Function to clear the search query, takes no arguments.
 * @param {Function} props.onInputChange - Function to handle input change, takes the event as an argument.
 * @param {Function} props.onInputKeyDown - Function to handle key down events, takes the event as an argument. Should handle hitting the "enter" key.
 * @param {Function} props.onHideMobileSearch - Function to hide the mobile search, takes no arguments.
 * @param {Function} props.onPopulateQueryButtonClick - Function to handle populate query button click, takes the new query as an argument.
 * @param {Function} props.onResultSearchButtonClick - Function to handle result search button click, takes the event as an argument.
 * @param {Function} props.onSearchButtonClick - Function to handle search button click, takes suggestion text, suggestion ID, and index as arguments.
 * @returns {JSX.Element} The rendered MegaMenuSearchBar component
 */
const MegaMenuSearchBar = ({
  type = "mobile",
  query = "",
  querySuggestions = { hits: [] },
  clearQuery = () => {},
  shouldFocus = false,
  shouldFocusCallback = () => {},
  onInputChange = (event) => {},
  onInputKeyDown = (event) => {},
  onHideMobileSearch = () => {},
  onPopulateQueryButtonClick = (newQuery) => {},
  onResultSearchButtonClick = (event) => {},
  onSearchButtonClick = (suggestionText, suggestionId, index) => {}
}) => {
  return (
    <>
      <form autoComplete="off">
        <div className={`row ${type === "mobile" ? "mx-1" : ""}`}>
          {type === "mobile" && (
            <div className="col-6">
              <button
                id="ssi-hide-mobile-search-button"
                onClick={onHideMobileSearch}
                className={`pl-0 pr-2 d-inline-block btn ${styles.buttonHideMobileSearch}`}
                tabIndex="-1"
              >
                <Icon name="arrow-left" class="fa-md text-gray-90" />
              </button>
            </div>
          )}

          <SearchBar
            id="ssi"
            idSuffix={type === "mobile" ? "mobile" : null}
            query={query}
            label="Search WaFd Bank"
            onInputChange={onInputChange}
            onInputKeyDown={onInputKeyDown}
            onSearchButtonClick={onSearchButtonClick}
            clearQuery={clearQuery}
            shouldFocus={shouldFocus}
            shouldFocusCallback={shouldFocusCallback}
          />
        </div>
      </form>
      <MegaMenuResultsContainer
        hits={querySuggestions?.hits || []}
        type={type}
        query={query}
        onPopulateQueryButtonClick={onPopulateQueryButtonClick}
        onResultSearchButtonClick={onResultSearchButtonClick}
      />
    </>
  );
};

export default MegaMenuSearchBar;

MegaMenuSearchBar.propTypes = {
  type: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  querySuggestions: PropTypes.object.isRequired,
  clearQuery: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onInputKeyDown: PropTypes.func.isRequired,
  onHideMobileSearch: PropTypes.func.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onResultSearchButtonClick: PropTypes.func.isRequired,
  onSearchButtonClick: PropTypes.func.isRequired
};
